import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Typography, message } from "antd";
import QuotePoster from "./QuotePoster";
import {
    downloadQuotePosterFromElement,
    ensureQuotePosterFontsLoaded,
    getQuotePosterAsset,
} from "../../utils/quotePoster";
import { useIsMobileDeliveryDevice } from "../../utils/device";
import { useTranslation } from "../../i18n";

const { Paragraph } = Typography;

function QuotePreviewPanel({ quoteDisplay, visible }) {
    const { t } = useTranslation();
    const isMobile = useIsMobileDeliveryDevice();
    const posterRef = useRef(null);
    const [posterReady, setPosterReady] = useState(false);
    const [posterError, setPosterError] = useState("");
    const [generating, setGenerating] = useState(false);
    const [savedImageAsset, setSavedImageAsset] = useState(null);
    const canShareImages =
        typeof navigator !== "undefined" &&
        Boolean(navigator.canShare) &&
        Boolean(navigator.share);

    useEffect(() => {
        if (!visible || !quoteDisplay) {
            setPosterReady(false);
            setPosterError("");
            return;
        }

        setPosterReady(false);
        setPosterError("");

        let cancelled = false;

        async function preparePoster() {
            try {
                await ensureQuotePosterFontsLoaded();
                if (!cancelled) {
                    setPosterReady(true);
                }
            } catch (error) {
                console.error("Quote poster font loading failed:", error);
                if (!cancelled) {
                    setPosterReady(true);
                }
            }
        }

        preparePoster();

        return () => {
            cancelled = true;
        };
    }, [visible, quoteDisplay]);

    const handleDownloadPoster = useCallback(async () => {
        if (!posterRef.current || !quoteDisplay || generating) {
            return;
        }

        setGenerating(true);

        try {
            if (isMobile) {
                const asset = await getQuotePosterAsset(
                    posterRef.current,
                    quoteDisplay.projectName,
                    quoteDisplay.quoteDateLabel
                );
                setSavedImageAsset(asset);
            } else {
                await downloadQuotePosterFromElement(
                    posterRef.current,
                    quoteDisplay.projectName,
                    quoteDisplay.quoteDateLabel
                );
                message.success(t("quote.preview.posterDownloaded"));
            }
        } catch (error) {
            console.error("Quote poster generation failed:", error);
            setPosterError(t("quote.preview.posterFailed"));
            message.error(t("quote.preview.posterDownloadFailed"));
        } finally {
            setGenerating(false);
        }
    }, [quoteDisplay, isMobile, generating, t]);

    const handleCloseSavedImage = useCallback(() => {
        setSavedImageAsset(null);
    }, []);

    const handleSaveToPhotos = useCallback(async () => {
        if (!savedImageAsset) {
            return;
        }

        try {
            const file = new File([savedImageAsset.blob], savedImageAsset.filename, {
                type: "image/png",
            });
            const sharePayload = {
                files: [file],
                title: quoteDisplay?.projectName || t("quote.preview.imageTabTitle"),
            };

            if (navigator.canShare) {
                if (!navigator.canShare(sharePayload)) {
                    message.info(t("quote.preview.longPressHint"));
                    return;
                }
                await navigator.share(sharePayload);
            } else if (navigator.share) {
                await navigator.share(sharePayload);
            } else {
                message.info(t("quote.preview.longPressHint"));
            }
        } catch (error) {
            if (error && error.name === "AbortError") {
                return;
            }
            console.error("Quote poster sharing failed:", error);
            message.info(t("quote.preview.longPressHint"));
        }
    }, [quoteDisplay, savedImageAsset, t]);

    const handleOpenSavedImage = useCallback(() => {
        if (!savedImageAsset) {
            return;
        }

        const opened = window.open(savedImageAsset.dataUrl, "_blank", "noopener");
        if (!opened) {
            window.location.href = savedImageAsset.dataUrl;
        }
    }, [savedImageAsset]);

    if (!visible) {
        return null;
    }

    return (
        <div className="quote-preview">
            <div className="quote-preview__actions">
                <Button
                    type="primary"
                    onClick={handleDownloadPoster}
                    loading={generating}
                    disabled={!posterReady || Boolean(posterError)}
                >
                    {isMobile
                        ? t("quote.preview.openPoster")
                        : t("quote.preview.downloadPoster")}
                </Button>
            </div>

            <div className="quote-preview__poster-wrap">
                {!posterReady && (
                    <p className="quote-preview__poster-status">{t("quote.preview.posterLoading")}</p>
                )}
                {posterError && (
                    <Paragraph type="danger" className="quote-preview__poster-status">
                        {posterError}
                    </Paragraph>
                )}
                <div className={posterReady ? "" : "quote-preview__poster--loading"}>
                    <QuotePoster ref={posterRef} quoteDisplay={quoteDisplay} />
                </div>
            </div>

            {savedImageAsset && (
                <div
                    className="quote-preview__overlay"
                    role="dialog"
                    aria-modal="true"
                    onClick={handleCloseSavedImage}
                >
                    <p className="quote-preview__overlay-hint">
                        {t("quote.preview.saveSheetHint")}
                    </p>
                    <img
                        className="quote-preview__overlay-img"
                        src={savedImageAsset.dataUrl}
                        alt={quoteDisplay.projectName || t("quote.preview.imageTabTitle")}
                        onClick={(event) => event.stopPropagation()}
                        draggable={false}
                    />
                    <div
                        className="quote-preview__overlay-actions"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {canShareImages && (
                            <Button type="primary" onClick={handleSaveToPhotos}>
                                {t("quote.preview.saveToPhotos")}
                            </Button>
                        )}
                        <Button onClick={handleOpenSavedImage}>
                            {t("quote.preview.openImage")}
                        </Button>
                        <Button onClick={handleCloseSavedImage}>
                            {t("quote.preview.closePreview")}
                        </Button>
                    </div>
                    <p className="quote-preview__overlay-fallback">
                        {t("quote.preview.longPressHint")}
                    </p>
                </div>
            )}
        </div>
    );
}

export default QuotePreviewPanel;
